import {useParams, Link} from 'react-router-dom'
import React from "react";

import { findObjectByKeyValue, numToPrice } from '../components/utils.js';
import {RangeSlider} from "../components/transfers/RangeSlider";
import {TeamDropdown} from "../components/common/Dropdown.jsx";
import {PlayersBlock} from "../components/transfers/PlayersBlock";
import {TransfersPageNav} from "../components/transfers/TransfersPageNav";
import {TransferFlowPopup} from "../components/transfers/Popups";
import { AuthContext } from '../context/AuthContext.jsx';
import { useActiveLeague } from '../hooks/useActiveLeague';

import TransferService from '../services/transferService';
import TeamService from '../services/teamService.js';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';

const getWyId = (item) => {
    const rawWyId = item?.wyId ?? item?.player?.wyId;
    if (rawWyId === null || typeof rawWyId === 'undefined') {
        return null;
    }

    const wyId = Number(rawWyId);
    return Number.isFinite(wyId) ? wyId : null;
};

const createEmptyTeamInfo = (leagueId = null) => ({
    boosted: false,
    startingBudget: 0,
    transferBudget: 0,
    remainingBudget: 0,
    squadSize: 0,
    id: null,
    name: '',
    leagueId,
    players: []
});

function TransfersSignedPage() {
    const { user } = React.useContext(AuthContext);
    const { activeLeague } = useActiveLeague();
    const leagueId = activeLeague?._id ?? activeLeague?.id ?? null;
    const isQuickplayLeague = activeLeague?.gameMode === 'quickplay';
    const queryClient = useQueryClient();

    const showFields = {
        position: "Pos",
        name : "Name",
        age : "Age",
        nationality : "Nationality",
        signedFor : "Signed for",
    }

    function filterObjects(objects, filters) {
        return objects.filter(obj => {
            const matchesName = filters.name ? obj.name.toLowerCase().includes(filters.name.toLowerCase()) : true;
            const matchesPosition = filters.position ? obj.position === filters.position : true;
            const matchesMinAge = filters.minAge !== null && obj.age !== null ? obj.age >= filters.minAge : true;
            const matchesMaxAge = filters.maxAge !== null && obj.age !== null ? obj.age <= filters.maxAge : true;
            const matchesMinVal = filters.minVal !== null ? obj.value >= filters.minVal : true;
            const matchesMaxVal = filters.maxVal !== null ? obj.value <= filters.maxVal : true;

            return matchesName && matchesPosition && matchesMinAge && matchesMaxAge && matchesMinVal && matchesMaxVal;
        });
    }

    function findMinMax(arr, field) {
        if (arr.length === 0) {
            return null; // Возвращаем null, если массив пустой
        }

        let min = arr[0][field];
        let max = arr[0][field];

        for (const obj of arr) {
            if (obj[field] < min) {
                min = obj[field];
            }
            if (obj[field] > max) {
                max = obj[field];
            }
        }

        return {
            min: min,
            max: max
        };
    }

    // Team info state
    const [teamInfo, setTeamInfo] = React.useState(() => createEmptyTeamInfo());

    // TanStack Query for transfer history
    const { data: transfers, isLoading: loading, error, refetch } = useQuery({
        queryKey: ['transferHistory', leagueId, teamInfo.id],
        queryFn: async () => {
            if (!leagueId || teamInfo.leagueId !== leagueId || !teamInfo.id || !teamInfo.name) {
                return [];
            }
            
            const response = await TransferService.getTransferHistory({
                leagueId,
                teamId: teamInfo.id,
                page: 1, 
                limit: 50 
            });
            
            const acquisitions = (response.transfers || []).filter(transfer => 
                transfer.toTeam && transfer.toTeam.name && teamInfo.name && transfer.toTeam.name === teamInfo.name
            );
            
            return acquisitions;
        },
        enabled: !!leagueId && teamInfo.leagueId === leagueId && !!teamInfo.id && !!teamInfo.name,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
    });

    // Now compute values directly using state (similar to TransfersOngoingPage pattern)
    const uniqueTitles = new Set();
    const currentRosterWyIds = React.useMemo(() => {
        return new Set(
            (teamInfo.players || [])
                .map(getWyId)
                .filter((wyId) => wyId !== null)
        );
    }, [teamInfo.players]);

    const transfersInfo = React.useMemo(() => {
        const seenWyIds = new Set();

        return (transfers || []).filter((transfer) => {
            const wyId = getWyId(transfer);
            if (wyId === null || !currentRosterWyIds.has(wyId) || seenWyIds.has(wyId)) {
                return false;
            }

            seenWyIds.add(wyId);
            return true;
        });
    }, [transfers, currentRosterWyIds]);
    
    const unicPositions = transfersInfo.reduce((acc, current) => {
        if (!uniqueTitles.has(current.position)) {
            uniqueTitles.add(current.position);
            acc.push({ id: acc.length + 1, title: current.position });
        }
        return acc;
    }, []);

    unicPositions.unshift({ id: 0, title: "All" });

    const ages = findMinMax(transfersInfo, 'age');
    const values = findMinMax(transfersInfo, 'value');

    const initFilters = {
        name: null,
        position: null,
        minAge: ages?.min || 18,
        maxAge: ages?.max || 40,
        minVal: values?.min || 1000000,
        maxVal: values?.max || 100000000,
    };

    const [filters, setFilters] = React.useState(initFilters);
    const [selectedPlayer, setSelectedPlayer] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [dropError, setDropError] = React.useState('');

    // Apply filters to transfers
    const filteredTransfers = filterObjects(transfersInfo, filters);

    // Update filters when data changes
    React.useEffect(() => {
        if (!loading && transfersInfo.length > 0) {
            const newAges = findMinMax(transfersInfo, 'age');
            const newValues = findMinMax(transfersInfo, 'value');
            
            setFilters(prevFilters => ({
                ...prevFilters,
                minAge: newAges?.min || 18,
                maxAge: newAges?.max || 40,
                minVal: newValues?.min || 1000000,
                maxVal: newValues?.max || 100000000,
            }));
        }
    }, [loading, transfersInfo]);

    const handleDrop = async () => {
        if (!selectedPlayer) {
            toast.error("No player selected to drop.");
            return;
        }
        if (!teamInfo || !teamInfo.id) {
            toast.error("Team information is not available.");
            return;
        }
        if (!leagueId || teamInfo.leagueId !== leagueId) {
            toast.error("League information is not available.");
            return;
        }
        const selectedWyId = getWyId(selectedPlayer);
        if (selectedWyId === null) {
            toast.error("Player information is not available.");
            return;
        }

        setSubmitting(true);
        setDropError('');
        try {
            await TeamService.dropPlayer(teamInfo.id, selectedWyId, leagueId);

            toast.success(`${selectedPlayer.name} has been dropped successfully.`);

            // Update current roster locally so the dropped player disappears immediately.
            setTeamInfo(prev => (
                prev.leagueId === leagueId && prev.id === teamInfo.id
                    ? {
                        ...prev,
                        squadSize: Math.max(0, prev.squadSize - 1),
                        players: (prev.players || []).filter(player => getWyId(player) !== selectedWyId),
                    }
                    : prev
            ));
            
            // Invalidate cached data (react-query) and also refresh team budget immediately
            queryClient.invalidateQueries({ queryKey: ['team', teamInfo.id] });
            queryClient.invalidateQueries({ queryKey: ['team-teamsheet', teamInfo.id] });
            queryClient.invalidateQueries({ queryKey: ['transferHistory', leagueId, teamInfo.id] });
            
            // Refetch the transfer history to get updated data
            refetch();

            // Fetch latest team info so budget numbers update without full page reload
            try {
                const refreshed = await TransferService.getTeamInfo(leagueId);
                setTeamInfo(prev => (
                    prev.leagueId === leagueId
                        ? {
                            ...prev,
                            startingBudget: typeof refreshed.data.startingBudget === 'number' ? refreshed.data.startingBudget : prev.startingBudget,
                            transferBudget: typeof refreshed.data.transferBudget === 'number' ? refreshed.data.transferBudget : prev.transferBudget,
                            remainingBudget: typeof refreshed.data.remainingBudget === 'number' ? refreshed.data.remainingBudget : prev.remainingBudget,
                            squadSize: typeof refreshed.data.squadSize === 'number' ? refreshed.data.squadSize : prev.squadSize,
                            id: refreshed.data.teamId || prev.id,
                            name: refreshed.data.name || prev.name,
                            leagueId,
                            players: Array.isArray(refreshed.data.players) ? refreshed.data.players : prev.players,
                        }
                        : prev
                ));
            } catch (refreshErr) {
                console.error('Failed to refresh team info after drop:', refreshErr);
            }

            setSelectedPlayer(null); 
            closePopup(); 
        } catch (err) {
            console.error("Error dropping player:", err);
            const errorMessage = (
                err.response?.data?.message ||
                (err.response?.status === 409
                    ? 'You cannot drop players while your match is live. Try again after full time.'
                    : err.message) ||
                'An unexpected error occurred while dropping the player.'
            );
            setDropError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    React.useEffect(() => {
        let cancelled = false;

        setTeamInfo(createEmptyTeamInfo(leagueId));
        setSelectedPlayer(null);

        const fetchTeamData = async () => {
            if (!user || !leagueId) return;
            try {
                const response = await TransferService.getTeamInfo(leagueId);
                if (cancelled) return;
                setTeamInfo({
                    boosted: response.data.boosted || false,
                    startingBudget: response.data.startingBudget || 0,
                    transferBudget: response.data.transferBudget || 0,
                    remainingBudget: response.data.remainingBudget || 0,
                    squadSize: response.data.squadSize || 0,
                    id: response.data.teamId,
                    name: response.data.name || '',
                    leagueId,
                    players: Array.isArray(response.data.players) ? response.data.players : [],
                });
            } catch (err) {
                if (cancelled) return;
                console.error('Error fetching team info:', err);
                setTeamInfo(createEmptyTeamInfo(leagueId));
                toast.error("Could not load team information.");
            }
        };

        fetchTeamData();

        return () => {
            cancelled = true;
        };
    }, [user, leagueId]);


    function setName (name) {
        if (filters.name !== name) {
            setFilters(prevFilters => ({
                ...prevFilters,
                name: name
            }));
        }
    }

    function setPosition (id) {
        const positionObj = findObjectByKeyValue(unicPositions, 'id', id);
        const position = positionObj && positionObj.title !== "All" ? positionObj.title : null;

        if (filters.position !== position) {
            setFilters(prevFilters => ({
                ...prevFilters,
                position: position,
            }));
        }
    }

    function setMinMaxAge(min, max) {
        if (filters.minAge !== min || filters.maxAge !== max) {
            setFilters(prevFilters => ({
                ...prevFilters,
                minAge: min,
                maxAge: max,
            }));
        }
    }

    function setMinMaxValue(min, max) {
        if (filters.minVal !== min || filters.maxVal !== max) {
            setFilters(prevFilters => ({
                ...prevFilters,
                minVal: min,
                maxVal: max,
            }));
        }
    }
    const dropRef = React.useRef();
    const rangeAgeRef = React.useRef();
    const rangeValRef = React.useRef();

    function clearFilters() {
        const currentAges = findMinMax(transfersInfo, 'age');
        const currentValues = findMinMax(transfersInfo, 'value');
        
        const resetFilters = {
            name: null,
            position: null,
            minAge: currentAges?.min || 18,
            maxAge: currentAges?.max || 40,
            minVal: currentValues?.min || 1000000,
            maxVal: currentValues?.max || 100000000,
        };
        
        setFilters(resetFilters);
        dropRef.current?.clear();
        rangeAgeRef.current?.clear();
        rangeValRef.current?.clear();
    }

    const [filtersOpen, setFiltersOpen] = React.useState(false);
    const [teamOpen, setTeamOpen] = React.useState(false);
    const [popOpen, setPopOpen] = React.useState(false);
    const [popEvent, setPopEvent] = React.useState(false);

    const closePopup = () => {
        setPopOpen(false);
        setSelectedPlayer(null);
        setDropError('');
    }

    const handlePopEvent = (event) => {
        setPopEvent(event);
    }

    const handlePopup = (event, player = null) => {
        setDropError('');
        if (player) {
            setSelectedPlayer(player);
        }
        setPopOpen(true);
        setPopEvent(event);
    }

    const handleBoost = async (option) => {
        setPopOpen(false);
        console.log('Boost option:', option);
        // setSubmitting(true);
        // try {
        //   await boostTeamAPI(); // Placeholder for actual API call
        //   toast.success("Team boosted!");
        //   queryClient.invalidateQueries({ queryKey: ['team', teamInfo.id] });
        // } catch (err) {
        //   toast.error("Failed to boost team.");
        // } finally {
        //   setSubmitting(false);
        // }
    }

    const handleSubmit = async () => {
        setPopOpen(false);
        console.log('Squad submitted');
        // setSubmitting(true);
        // try {
        //   await submitSquadAPI(); // Placeholder for actual API call
        //   toast.success("Squad submitted successfully!");
        // } catch (err) {
        //   toast.error("Failed to submit squad.");
        // } finally {
        //   setSubmitting(false);
        // }
    }

    return (
        <div className="page-market">
            <div className="wrapper">
                <h1>
                    TRANSFER MARKET
                </h1>

                <TransfersPageNav />

                <div className="market-grid">

                    <div className="filters-col">
                        <div className={`filters ${filtersOpen ? 'active' : ''}`}>
                            <button className="show-filters"
                                    onClick={() => {setFiltersOpen(true)}}
                            >
                                SHOW FILTERS
                            </button>

                            <div className="form-block">
                                <div className="filters-container">
                                    <div className="form-group search-group">
                                        <label htmlFor="i1">SEARCH BY NAME *</label>
                                        <input id="i1" type="text"
                                               value={filters.name === null ? '' : filters.name}
                                               onChange={(event) => {setName(event.target.value)}}
                                        />
                                        <span className="ico icon-search" />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="s1">POSITION</label>
                                        <TeamDropdown ref={dropRef} props={unicPositions} setId={(id) => setPosition(id)} startVal={'POSITION'}/>
                                    </div>

                                    <div className="range-sliders">
                                        <div className="form-group range-block">
                                            <h4>AGE</h4>
                                            <RangeSlider
                                                ref={rangeAgeRef}
                                                min={ages?.min || 18}
                                                max={ages?.max || 40}
                                                sendMinMax={setMinMaxAge}
                                            />
                                        </div>

                                        <div className="form-group range-block">
                                            <h4>VALUE</h4>
                                            <RangeSlider
                                                ref={rangeValRef}
                                                min={values?.min || 1000000}
                                                max={values?.max || 100000000}
                                                sendMinMax={setMinMaxValue}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="filters-bottom">
                                    <button className="hide-filters"
                                            onClick={() => {setFiltersOpen(false)}}
                                    >
                                        HIDE FILTERS
                                    </button>
                                    <div className="reset-col">
                                        <button className="reset-filters"
                                                onClick={() => {clearFilters()}}
                                        >
                                            Clear <span className="desc-text">filters</span> <span className="ico icon-s-cross" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="list-col">
                        {loading ? (
                            <div>Loading transfers...</div>
                        ) : error ? (
                            <div>Error loading transfers: {error?.message || error}</div>
                        ) : (
                            <>
                                <PlayersBlock
                                    players={filteredTransfers}
                                    unsignable={true} // Indicate players on this page can be dropped
                                    showFields={showFields}
                                    setSelectedPlayer={setSelectedPlayer}
                                    handlePopup={handlePopup}
                                    teamId={teamInfo.id}
                                />
                                {filteredTransfers.length === 0 && !loading && (
                                    <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                        No transfer history found.
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className={`team-col`}>
                        <div className={`team-container ${teamOpen ? 'active' : ''}`}  >
                            <button className="show-team"
                                    onClick={() => {setTeamOpen(true)}}
                            >
                                SHOW TEAM
                            </button>
                            <button className="hide-team"
                                    onClick={() => {setTeamOpen(false)}}
                            >
                                HIDE TEAM
                            </button>
                            <div className="info-row">
                                <h4>My team</h4>
                                <div className="info-container">
                                    <div className="info-block">
                                        <div className="i-title boost-title">
                                            Transfer budget
                                            {/* BOOST button temporarily hidden - will be needed in future */}
                                            {/* <button
                                                type="button"
                                                className={`submit-btn trans-btn ${teamInfo.boosted ? 'disabled' : ''}`}
                                                onClick={() => { handlePopup('boost') }}
                                                disabled={teamInfo.boosted || submitting}
                                            >
                                                BOOST
                                            </button> */}
                                        </div>
                                        <div className="i-value">
                                            <span className="ico icon-nova" />
                                            {numToPrice(
                                                Number.isFinite(teamInfo.startingBudget) && teamInfo.startingBudget > 0
                                                    ? teamInfo.startingBudget
                                                    : teamInfo.transferBudget
                                            )}
                                        </div>
                                    </div>
                                    <div className="info-block">
                                        <div className="i-title">Remaining budget</div>
                                        <div className="i-value">
                                            <span className="ico icon-nova" />
                                            {numToPrice(teamInfo.remainingBudget)}
                                        </div>
                                        <div className="i-note">Drop or sell players to get transfer money back.</div>
                                    </div>
                                    <div className="info-block">
                                        <div className="i-title">Squad size</div>
                                        <div className="i-value">
                                            {teamInfo.squadSize} / 22
                                        </div>
                                    </div>
                                </div>
                                <div className={`tip ${teamInfo.squadSize >= 22 ? 'tip-success' : 'tip-alert'}`}>
                                    Minimum squad size: 22 <span className="ico icon-r-check" />
                                </div>
                            </div>

                        </div>
                    </div>
                </div> 
            </div> 

            <TransferFlowPopup 
                info={teamInfo} 
                popEvent={popEvent} 
                setPopEvent={handlePopEvent}
                popOpen={popOpen} 
                closePopup={closePopup} 
                handleSubmit={handleSubmit} 
                handleBoost={handleBoost}
                selectedPlayer={selectedPlayer}
                handleDrop={handleDrop}
                submitting={submitting}
                submitError={dropError}
                isQuickplayLeague={isQuickplayLeague}
            />
        </div> 
    );
}

export {TransfersSignedPage};
